import { useState } from "react";
import { useLocation } from "react-router-dom";

import { useSafeGoBack } from "../../hooks/SafeGoBack";
import BackIcon from "../../icons/Back";
import { DESKTOP_ROOT_ROUTES, normalizePath } from "../AppShell";
import { SmallCircleContainer } from "../CircleContainer/styles";
import { UnsavedChangesConfirmModal } from "../UnsavedChangesConfirmModal";
import { Container, Title, TitleSection } from "./styles";

type Props = {
  title?: string;
  onClickBack?: () => void;
  hasUnsavedChanges?: boolean | (() => boolean);
  onConfirmExitUnsavedChanges?: () => void;
  isConfirmExitUnsavedChanges?: boolean;
  titleConfirmExitModal?: string;
  buttonTitleConfirmExitModal?: string;
  cancelTitleConfirmExitModal?: string;
  rightElement?: React.ReactNode;
  /** Hides the back button on every screen size (e.g. screens reachable from the main nav). */
  hideBack?: boolean;
  /** Hides the whole header on mobile, where the bottom nav already identifies the screen. */
  hideOnMobile?: boolean;
};

const Header = ({
  title,
  onClickBack,
  hasUnsavedChanges,
  onConfirmExitUnsavedChanges,
  titleConfirmExitModal,
  buttonTitleConfirmExitModal,
  cancelTitleConfirmExitModal,
  rightElement,
  hideBack,
  hideOnMobile,
}: Props) => {
  const safeGoBack = useSafeGoBack();
  const isDesktopRoot = DESKTOP_ROOT_ROUTES.includes(normalizePath(useLocation().pathname));

  const hasUnsavedChangesFn = (): boolean => {
    return typeof hasUnsavedChanges === "function" ? hasUnsavedChanges() : hasUnsavedChanges === true;
  };

  const [showConfirmUnsavedChangesModal, setShowConfirmUnsavedChangesModal] = useState(false);

  const onClickBackDefaultEventHandler = () => {
    if (hasUnsavedChangesFn()) {
      setShowConfirmUnsavedChangesModal(true);
    } else {
      if (onClickBack !== undefined && typeof onClickBack === "function") {
        onClickBack();
        return;
      }

      safeGoBack();
    }
  };

  return (
    <>
      {showConfirmUnsavedChangesModal && (
        <UnsavedChangesConfirmModal
          onConfirmExit={() => {
            setShowConfirmUnsavedChangesModal(false);
            if (onConfirmExitUnsavedChanges) {
              onConfirmExitUnsavedChanges();
            } else {
              safeGoBack();
            }
          }}
          onCloseModal={() => setShowConfirmUnsavedChangesModal(false)}
          title={titleConfirmExitModal}
          confirmButtonTitle={buttonTitleConfirmExitModal}
          cancelTitle={cancelTitleConfirmExitModal}
        />
      )}
      <Container $desktopRoot={isDesktopRoot} $empty={!title && !rightElement} $hideOnMobile={hideOnMobile}>
        {!hideBack && (
          <SmallCircleContainer
            className="back"
            onClick={onClickBackDefaultEventHandler}
            $color="var(--color-neutral-base)"
            cursor="pointer">
            <BackIcon color="var(--color-neutral-white)" />
          </SmallCircleContainer>
        )}
        <TitleSection>
          <Title>{title}</Title>
        </TitleSection>
        {rightElement}
      </Container>
    </>
  );
};

export default Header;
